import { useEffect, useState } from "react";
import { IWebsiteBuilderTableBodyRow, IWebsiteBuilderTableHeadRow } from "../../nodes/container/websiteBuilderTable";
import { useDragLayer, XYCoord } from "react-dnd";
import { IWebsiteBuilderSectionEntityViewBoundingBox } from "../../designSpace/views/hooks/sectionEntity/helpers/sectionEntityViewBoundingBox";
import { TTableBuilderDomNodeRefMap } from "./useTableBuilderDomNodeRefMapRegistration";
import { find } from "lodash";
import { isTableBuilderColumnDragItem, isTableBuilderRowDragItem } from "../views/TableBuilderRowView";

interface IUseTableBuilderDragLayerContextArgs {
  containerBoundingBox: IWebsiteBuilderSectionEntityViewBoundingBox,
  domNodeRefMap: TTableBuilderDomNodeRefMap,
  tableNodeId: string,
  tableRef: React.MutableRefObject<HTMLTableElement>,
  tableHeadRow: IWebsiteBuilderTableHeadRow,
  tableBodyRows: IWebsiteBuilderTableBodyRow[],
}

interface ITableBuilderDragLayerRowDomTarget {
  id: string;
  top: number;
  center: number;
  bottom: number;
}

interface ITableBuilderDragLayerColumnDomTarget {
  index: number;
  left: number;
  center: number;
  right: number;
}

type TTableBuilderDragLayerRowTargetAnchor = "above" | "below";
type TTableBuilderDragLayerColumnTargetAnchor = "before" | "after";

interface ITableBuilderDragLayerRowTarget {
  anchor: TTableBuilderDragLayerRowTargetAnchor;
  id: string;
  y: number;
}

interface ITableBuilderDragLayerColumnTarget {
  anchor: TTableBuilderDragLayerColumnTargetAnchor;
  index: number;
  x: number;
}

interface ITableBuilderDragLayerStandbyContext {
  type: "standby";
}

interface ITableBuilderDragLayerRowActiveContext {
  type: "row-active";
  bodyRow: IWebsiteBuilderTableBodyRow;
  clientOffset: XYCoord;
  target: ITableBuilderDragLayerRowTarget;
}

interface ITableBuilderDragLayerColumnActiveContext {
  type: "column-active";
  columnIndex: number;
  clientOffset: XYCoord;
  target: ITableBuilderDragLayerColumnTarget;
}

export type TTableBuilderDragLayerContext =
  | ITableBuilderDragLayerStandbyContext
  | ITableBuilderDragLayerRowActiveContext
  | ITableBuilderDragLayerColumnActiveContext

export function useTableBuilderDragLayerContext(args: IUseTableBuilderDragLayerContextArgs): TTableBuilderDragLayerContext {
  const { isDragging, clientOffset, item } = useDragLayer(monitor => {
    return {
      isDragging: monitor.isDragging(),
      clientOffset: monitor.getClientOffset(),
      item: monitor.getItem<unknown>(),
    };
  });

  const [rowTarget, setRowTarget] = useState<ITableBuilderDragLayerRowTarget>(null);
  const [columnTarget, setColumnTarget] = useState<ITableBuilderDragLayerColumnTarget>(null);

  const rowActive =
    isDragging &&
    isTableBuilderRowDragItem(item) &&
    item.tableNodeId === args.tableNodeId &&
    clientOffset != null;

  const columnActive =
    isDragging &&
    isTableBuilderColumnDragItem(item) &&
    item.tableNodeId === args.tableNodeId &&
    clientOffset != null;

  useEffect(() => {
    if (rowActive) {
      const rowDomTargets: ITableBuilderDragLayerRowDomTarget[] = [];

      const tableBoundingRect = args.tableRef.current?.getBoundingClientRect();

      if (tableBoundingRect == null) {
        return;
      }

      const bottomCutoff = tableBoundingRect.top + args.containerBoundingBox.height;

      args.tableBodyRows.forEach(row => {
        const domNode = args.domNodeRefMap.current.get(row.id);
        const boundingRect = domNode?.getBoundingClientRect();

        if (boundingRect != null && boundingRect.bottom < bottomCutoff) {
          rowDomTargets.push({
            id: row.id,
            top: boundingRect.top,
            center: boundingRect.top + (boundingRect.height / 2),
            bottom: boundingRect.bottom,
          });
        }
      });
      
      const rowDomTarget = find(rowDomTargets, rowTarget => clientOffset.y < rowTarget.center);
      
      if (rowDomTarget != null) {
        setRowTarget({
          anchor: "above",
          id: rowDomTarget.id,
          y: rowDomTarget.top,
        });
      } else {
        const lastRowDomTarget = rowDomTargets[rowDomTargets.length - 1];
  
        if (lastRowDomTarget != null) {
          setRowTarget({
            anchor: "below",
            id: lastRowDomTarget.id,
            y: lastRowDomTarget.bottom,
          });
        }
      }
    }

    return () => {
      if (rowActive) {
        setRowTarget(null);
      }
    };
  }, [rowActive, args.containerBoundingBox.height, args.tableBodyRows, clientOffset]);

  useEffect(() => {
    if (columnActive) {
      const columnDomTargets: ITableBuilderDragLayerColumnDomTarget[] = [];

      args.tableHeadRow?.cells.forEach((cell, index) => {
        const domNode = args.domNodeRefMap.current.get(cell.id);

        if (domNode != null) {
          const boundingRect = domNode.getBoundingClientRect();

          columnDomTargets.push({
            index,
            left: boundingRect.left,
            center: boundingRect.left + (boundingRect.width / 2),
            right: boundingRect.right,
          });
        }
      });

      const targetDomColumn = find(columnDomTargets, columnTarget => clientOffset.x < columnTarget.center);

      if (targetDomColumn != null) {
        setColumnTarget({
          anchor: "before",
          index: targetDomColumn.index,
          x: targetDomColumn.left,
        });
      } else {
        const lastColumnDomTarget = columnDomTargets[columnDomTargets.length - 1];

        if (lastColumnDomTarget != null) {
          setColumnTarget({
            anchor: "after",
            index: lastColumnDomTarget.index,
            x: lastColumnDomTarget.right,
          });
        }
      }
    }

    return () => {
      if (columnActive) {
        setColumnTarget(null);
      }
    };
  }, [columnActive, args.tableHeadRow, clientOffset]);

  if (rowActive) {
    return {
      type: "row-active",
      bodyRow: item.bodyRow,
      clientOffset,
      target: rowTarget,
    };
  } else if (columnActive) {
    return {
      type: "column-active",
      columnIndex: item.columnIndex,
      clientOffset,
      target: columnTarget,
    };
  } else {
    return {
      type: "standby",
    };
  }
}